import { BREAKPOINTS } from "../../config/breakpoints";
import React, { useEffect, useRef, useState } from "react";
import XookeeperService from "../Services/XookeeperService";
import { errorNotification, successNotification } from "../../Generic/Notifications";
import { Table, Thead, Trow, Tcol, Tbody } from "../../Generic/Table";
import EmptyState from "../../Generic/EmptyState";
import styled from "styled-components";
import close from "../../Assets/Close.png";
import Loader from "../../Generic/Loader";
import { CSSTransition } from "react-transition-group";
import Button from "../../Generic/Button";
import SuccessButton from "../../Generic/SuccessButton";
import LandingConnector from "../../Landing/Connector/LandingConnector";
import { useNavigate } from "react-router-dom";
import { SELECTION_TYPE, SOURCE, STEP_INPUT_TYPE } from "./Constants/steps.constants";
import Checkbox from "../../Generic/Checkbox";
import Dropdown from "../../Generic/Dropdown";
import InputBox from "../../Generic/InputBox";
import FetchInputDataButton from "./Components/FetchInputDataButton";
import { zIndex } from "../../config/zIndex";

const Div = styled.div`
	background: ${(props) => props.background};
	color: ${(props) => props.color};
	position: ${(props) => props.position};
	width: ${(props) => props.width};
	display: flex;
	flex-direction: ${(props) => props.flexDirection};
	justify-content: ${(props) => props.jContent};
	align-items: ${(props) => props.alignItem};
	padding: ${(props) => props.padding};
	margin: ${(props) => props.margin};
	text-align: ${(props) => props.txtAlign};
	font-size: ${(props) => props.fontSize};
	gap: ${(props) => props.gap};
	overflow-y: ${(props) => props.overflowY};
	overflow-x: ${(props) => props.overflowX};
	height: ${(props) => props.height};
	max-height: ${(props) => props.maxHeight};
	min-height: ${(props) => props.minHeight};
	max-width: ${(props) => props.maxWidth};
	white-space: ${(props) => props.whiteSpace};
	border: ${(props) => props.border};
	border-radius: ${(props) => props.borderRadius};
	@media (max-width: ${BREAKPOINTS.desktop}px) {
		justify-content: ${(props) => props.jContentBefore1000};
	}
`;

const Span = styled.span`
	font-size: ${(props) => props.fontSize};
	font-weight: ${(props) => props.fontWeight};
	font-family: ${(props) => props.fontFamily};
	color: ${(props) => props.color};
	margin: ${(props) => props.margin};
	padding: ${(props) => props.padding};
`;

const Page = styled.div`
	height: 100%;
	width: 100%;
	display: flex;
	flex-direction: column;
	padding: 0 1.5rem;
	overflow-y: auto;
	@media (max-width: ${BREAKPOINTS.tablet}px) {
		padding: 0 1rem;
	}
`;

const Header = styled.div`
	display: flex;
	font-size: 2rem;
	@media (max-width: ${BREAKPOINTS.tablet}px) {
		font-size: 1.5rem;
	}
`;

const Overlay = styled.div`
	position: fixed;
	display: flex;
	justify-content: center;
	align-items: center;
	background: rgba(0, 0, 0, 0.3);
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: ${zIndex.modalBackdrop};
`;

const Modal = styled.div`
	display: flex;
	flex-direction: column;
	background: #ffffff;
	height: max-content;
	width: ${(props) => props.width || "22rem"};
	max-width: 40rem;
	border-radius: 10px;
	overflow-x: hidden;
	z-index: ${zIndex.modal};
	@media (max-width: ${BREAKPOINTS.tablet}px) {
		width: 95% !important;
	}
`;

const CenteredContainer = styled.div`
	display: flex;
	flex: 1;
	justify-content: center;
	align-items: center;
`;

const COMPONENTS = {
	TASKS: "TASKS",
	TASK_MODAL: "TASK_MODAL",
	CONFIRM_MODAL: "CONFIRM_MODAL",
	STEPS_MODAL: "STEPS_MODAL",
};

const COMPONENT_STATES = {
	LOADING: "LOADING",
	ERROR: "ERROR",
	OK: "OK",
	HIDDEN: "HIDDEN",
};

const INITIAL_COMPONENT_STATES = {
	[COMPONENTS.TASKS]: COMPONENT_STATES.LOADING,
	[COMPONENTS.TASK_MODAL]: COMPONENT_STATES.HIDDEN,
	[COMPONENTS.CONFIRM_MODAL]: COMPONENT_STATES.HIDDEN,
	[COMPONENTS.STEPS_MODAL]: COMPONENT_STATES.HIDDEN,
};

function parseMessages(messageList, index, updateIndex) {
	if (!messageList?.length) return;

	let messagesJsx = [];

	for (const message of messageList) {
		const { text, selectionType } = message;
		messagesJsx.push(
			<Div padding="0" gap="1rem" width="100%" >
				<Span>{index}.</Span>
				<Div jContent="space-between" width="100%">
					<Span>{text}</Span>
					{selectionType === SELECTION_TYPE.MARK ? <Checkbox /> : ""}
				</Div>
			</Div>
		);
		index++;
	}

	updateIndex(index);

	return messagesJsx;
}

function restructureOptions(options) {
	let optionsList = [];

	if (options?.length) {
		for (const key of options) {
			optionsList.push({ head: key, value: key });
		}
	}

	return optionsList;
}

function parseInputs(inputKeys, onChange, workflowId, index, updateIndex) {
	if (!inputKeys?.length) return;

	let inputsJsx = [];
	for (const input of inputKeys) {
		const { name, text, type, options, source } = input;

		switch (type) {
			case STEP_INPUT_TYPE.SELECT_FROM_ENUM:
				const optionsList = restructureOptions(options);
				inputsJsx.push(
					<Div padding="0" gap="1rem" width="100%" >
						<Span>{index}.</Span>
						<Div flexDirection="column">
							<Span>{text}</Span>
							{source === SOURCE.INTERNAL ? (
								<FetchInputDataButton buttonText={`Fetch ${name}`} inputKey={name} workflowId={workflowId} />
							) : (
								""
							)}
							<Dropdown margin="1rem 0 0 0" optionsList={optionsList} onChange={(value) => onChange(name, value)} />
						</Div>
					</Div>
				);
				break;

			default:
				inputsJsx.push(
					<Div padding="0" gap="1rem" width="100%" >
						<Span>{index}.</Span>
						<Div flexDirection="column" padding="0">
							<Span>{text}</Span>
							{source === SOURCE.INTERNAL ? <FetchInputDataButton inputKey={name} workflowId={workflowId} /> : ""}
							<InputBox placeholder={name} margin="1rem 0 0 0" type="text" onChange={(e) => onChange(name, e.target.value)} />
						</Div>
					</Div>
				);
				break;
		}

		index++;
	}

	updateIndex(index)

	return inputsJsx;
}

const XookeeperPlannedTasks = (props) => {
	const [componentStates, setComponentStates] = useState(INITIAL_COMPONENT_STATES);
	const [tasks, setTasks] = useState([]);
	const [activeTaskData, setActiveTaskData] = useState({});
	const [page, setPage] = useState(1);
	const [lastElement, setLastElement] = useState();
	const hasMore = useRef(true);
	const navigate = useNavigate();

	function updateComponentState(component, state) {
		if (!(component in COMPONENTS) || !(state in COMPONENT_STATES)) return;
		setComponentStates((prev) => ({ ...prev, [component]: state }));
	}

	async function getPlannedTasks(handleLoading = false, clearTasks = false) {
		if (handleLoading) updateComponentState(COMPONENTS.TASKS, COMPONENT_STATES.LOADING);

		if (clearTasks) setTasks([]);

		const payload = {
			kioskId: props.machineID,
			statuses: [3],
			page,
			size: 10,
		};

		const { data, error } = await XookeeperService.getPlannedTasks(payload);
		if (data) {
			const { respWfList, count } = data;
			setTasks((prev) => (prev.length ? [...prev, ...respWfList] : respWfList));

			if (count <= tasks.length) {
				hasMore.current = false;
			}

			updateComponentState(COMPONENTS.TASKS, COMPONENT_STATES.OK);
		}
		if (error) {
			errorNotification(error);
			updateComponentState(COMPONENTS.TASKS, COMPONENT_STATES.ERROR);
		}
	}

	async function completeStepForTask() {
		const payload = activeTaskData?.payload || {};
		const { data, error } = await XookeeperService.markStepAsCompleted(activeTaskData.workflowId, payload);
		if (data) {
			successNotification(
				`${activeTaskData.activeStepName || activeTaskData.activeStepId} completed for #${activeTaskData.workflowId}`
			);
			setActiveTaskData({});
			updateComponentState(COMPONENTS.CONFIRM_MODAL, COMPONENT_STATES.HIDDEN);
			updateComponentState(COMPONENTS.TASK_MODAL, COMPONENT_STATES.HIDDEN);
			updateComponentState(COMPONENTS.STEPS_MODAL, COMPONENT_STATES.HIDDEN);
			getPlannedTasks(true, true);
		}
		if (error) {
			closeConfirmModal();
			errorNotification(error);
		}
	}

	async function completeStepWithBranching() {
		const payload = activeTaskData?.payload || {};
		const { data, error } = await XookeeperService.completeBranchedStep(activeTaskData.workflowId, payload);
		if (data) {
			successNotification(
				`${activeTaskData.activeStepName || activeTaskData.activeStepId} completed for #${activeTaskData.workflowId}`
			);
			setActiveTaskData({});
			updateComponentState(COMPONENTS.CONFIRM_MODAL, COMPONENT_STATES.HIDDEN);
			updateComponentState(COMPONENTS.TASK_MODAL, COMPONENT_STATES.HIDDEN);
			getPlannedTasks(true, true);
		}
		if (error) {
			closeConfirmModal();
			errorNotification(error);
		}
	}

	async function checkForBranching() {
		updateComponentState(COMPONENTS.CONFIRM_MODAL, COMPONENT_STATES.LOADING);

		const { data, error } = await XookeeperService.checkForBranching(activeTaskData.workflowId);
		if (data) {
			const { isBranching } = data;
			if (isBranching) {
				setActiveTaskData((prev) => ({ ...prev, isBranching, payload: { inputData: {} } }));
				await getRequiredStepsForWorkflow();
				updateComponentState(COMPONENTS.CONFIRM_MODAL, COMPONENT_STATES.HIDDEN);
				updateComponentState(COMPONENTS.TASK_MODAL, COMPONENT_STATES.OK);
			} else {
				completeStepForTask();
			}
		}
		if (error) {
			errorNotification(error);
			updateComponentState(COMPONENTS.CONFIRM_MODAL, COMPONENT_STATES.HIDDEN);
		}
	}

	function handleMarkAsCompletedButtonClick(activeTaskData) {
		if (activeTaskData?.isBranching) {
			completeStepWithBranching();
		} else {
			openConfirmModal();
		}
	}

	async function getRequiredStepsForWorkflow() {
		const { data, error } = await XookeeperService.getStepsRequiredToCompleteStep(activeTaskData.workflowId);
		if (data) {
			setActiveTaskData((prev) => ({ ...prev, stepsRequired: data }));
		}
		if (error) {
			errorNotification(error);
		}
	}

	function renderTasks(data, componentState) {
		switch (componentState) {
			case COMPONENT_STATES.LOADING:
				return (
					<CenteredContainer>
						<Loader />
					</CenteredContainer>
				);

			case COMPONENT_STATES.HIDDEN:
				return;

			case COMPONENT_STATES.ERROR:
				return <EmptyState retry={getPlannedTasks} />;

			case COMPONENT_STATES.OK:
				if (tasks.length) {
					let tasksRenderData = data.map((task, index, array) => {
						const { workflowId, startDate, duration, taskType, assignee, status } = task;
						return (
							<Trow
								breakpoint="800px"
								ref={(node) => (index === array.length - 1 ? setLastElement(node) : null)}
								onClick={() => openTaskModal(task)}>
								<Tcol title="Task ID" breakpoint="800px" borderRadius="10px 0 0 10px">{workflowId}</Tcol>
								<Tcol title="Start Time" breakpoint="800px">{startDate}</Tcol>
								<Tcol title="Duration Allocated" breakpoint="800px">{duration}</Tcol>
								<Tcol title="Task Type" breakpoint="800px">{taskType}</Tcol>
								<Tcol title="Assigned to" breakpoint="800px">{assignee}</Tcol>
								<Tcol title="Status" breakpoint="800px" borderRadius="0 10px 10px 0">{status}</Tcol>
							</Trow>
						);
					});
					return (
						<Table breakpoint="800px">
							<thead>
								<Trow head breakpoint="800px">
									<Thead>Task ID</Thead>
									<Thead>Start Time</Thead>
									<Thead>Duration Allocated</Thead>
									<Thead>Task Type</Thead>
									<Thead>Assigned to</Thead>
									<Thead>Status</Thead>
								</Trow>
							</thead>
							<Tbody>{tasksRenderData}</Tbody>
						</Table>
					);
				} else {
					return <EmptyState />;
				}

			default:
				return;
		}
	}

	function openConfirmModal() {
		updateComponentState(COMPONENTS.TASK_MODAL, COMPONENT_STATES.HIDDEN);
		updateComponentState(COMPONENTS.CONFIRM_MODAL, COMPONENT_STATES.OK);
	}

	function closeConfirmModal() {
		updateComponentState(COMPONENTS.CONFIRM_MODAL, COMPONENT_STATES.HIDDEN);
		updateComponentState(COMPONENTS.TASK_MODAL, COMPONENT_STATES.OK);
	}

	function renderTaskModal(data, componentState, onInputChange) {
		if (componentState === COMPONENT_STATES.HIDDEN) return;

		let formRender = [];

		function closeModal() {
			if (componentState === COMPONENT_STATES.LOADING) return;
			updateComponentState(COMPONENTS.TASK_MODAL, COMPONENT_STATES.HIDDEN);
		}

		if (data?.stepsRequired) {

			let stepIndex = 1;

			const updateIndex = (newIndex) => stepIndex = newIndex;

			const messages = parseMessages(data?.stepsRequired?.messageList, stepIndex, updateIndex);
			formRender.push(messages);

			const inputs = parseInputs(data?.stepsRequired?.inputKeys, onInputChange, data.workflowId, stepIndex, updateIndex);
			formRender.push(inputs);
		}

		return (
			<Modal width="90%">
				<Div background="rgba(45, 35, 66, 0.8)" color="#fff" padding="1rem" jContent="space-between">
					<Span fontSize="1.4rem">#{data.workflowId}</Span>
					<img
						style={{
							height: "1.5rem",
							filter: "invert(97%) sepia(0%) saturate(7500%) hue-rotate(126deg) brightness(111%) contrast(100%)",
						}}
						src={close}
						alt="close"
						onClick={closeModal}
					/>
				</Div>
				<Div padding="1.5rem" gap="1.5rem" flexDirection="column">
					<Span fontWeight="500" >Current Page : {data.activeStepName || data.activeStepId || "-"}</Span>
					{formRender.length ? (
						<>
							<Span fontWeight="500" >Steps to Complete :</Span>
							<Div flexDirection="column" alignItem="flex-start" gap="1rem" >
								{formRender}
							</Div>
						</>
					) : (
						""
					)}
					<Div jContent="flex-end" gap="1.5rem">
						<SuccessButton
							disabled={componentState === COMPONENT_STATES.LOADING}
							onClick={() => handleMarkAsCompletedButtonClick(activeTaskData)}>
							Mark As Completed
						</SuccessButton>
					</Div>
				</Div>
			</Modal>
		);
	}

	function renderConfirmModal(componentState) {
		return (
			<>
				<Div background="rgba(45, 35, 66, 0.8)" color="#fff" padding="1rem" jContent="space-between">
					<Span fontSize="1.2rem">Are you sure you want to mark this step as completed?</Span>
					<img
						style={{
							height: "1.5rem",
							filter: "invert(97%) sepia(0%) saturate(7500%) hue-rotate(126deg) brightness(111%) contrast(100%)",
						}}
						src={close}
						alt="close"
						onClick={closeConfirmModal}
					/>
				</Div>
				<Div jContent="flex-end" padding="1rem">
					<Div margin="0.5rem">
						<Button onClick={closeConfirmModal} disabled={componentState === COMPONENT_STATES.LOADING}>
							Cancel
						</Button>
					</Div>
					<Div margin="0.5rem">
						<SuccessButton disabled={componentState === COMPONENT_STATES.LOADING} onClick={checkForBranching}>
							Confirm
						</SuccessButton>
					</Div>
				</Div>
			</>
		);
	}

	function openTaskModal(taskData) {
		setActiveTaskData(taskData);
		updateComponentState(COMPONENTS.TASK_MODAL, COMPONENT_STATES.OK);
	}

	function handleStepsInput(key, value) {
		setActiveTaskData((prev) => {
			const prevPayload = prev?.payload || {};
			const prevInputKeys = prevPayload.inputData || {};

			return {
				...prev,
				payload: {
					...prevPayload,
					inputData: {
						...prevInputKeys,
						[key]: value,
					},
				},
			};
		});
	}
	//Creating a new observer which increments the page number once the observed element is fully visible
	const observer = useRef(
		new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					if (hasMore.current) {
						setPage((page) => page + 1);
					}
				}
			},
			{
				//Threshold set to 1 for the page to increment only when the element is 100% visible
				//Threshold = % of element to be visible / 100
				threshold: 1,
			}
		)
	);

	useEffect(() => {
		//Fires everytime page changes
		getPlannedTasks(true);
	}, [page]);

	useEffect(() => {
		//Update the element on which the call is made and set the observer on it once the list re-renders
		const currentElement = lastElement;
		const currentObserver = observer.current;

		if (currentElement) {
			currentObserver.observe(currentElement);
		}

		//Remove the observer once user exits the page
		return () => {
			if (currentElement) {
				currentObserver.unobserve(currentElement);
			}
		};
	}, [lastElement]);

	return (
		<Page>
			<Div margin="1.5rem 0 0 0" jContent="space-between" alignItem="center">
				<Header>Recent Tasks</Header>
				<Button onClick={() => navigate(`/${props.machineID}/xookeeper/tasks/past`)}>Past Tasks</Button>
			</Div>
			{renderTasks(tasks, componentStates[COMPONENTS.TASKS])}
			<CSSTransition
				in={componentStates[COMPONENTS.TASK_MODAL] !== COMPONENT_STATES.HIDDEN}
				timeout={300}
				classNames={"module"}
				unmountOnExit>
				<Overlay>{renderTaskModal(activeTaskData, componentStates[COMPONENTS.TASK_MODAL], handleStepsInput)}</Overlay>
			</CSSTransition>
			<CSSTransition
				in={componentStates[COMPONENTS.CONFIRM_MODAL] !== COMPONENT_STATES.HIDDEN}
				timeout={300}
				classNames={"module"}
				unmountOnExit>
				<Overlay>
					<Modal>{renderConfirmModal(componentStates[COMPONENTS.CONFIRM_MODAL])}</Modal>
				</Overlay>
			</CSSTransition>
		</Page>
	);
};

export default LandingConnector(XookeeperPlannedTasks);
